import React, { useState } from 'react';

const APGARCriteria = [
  {
    name: 'Appearance',
    description: 'Color',
    scores: [
      { value: 0, description: 'Blue or pale all over' },
      { value: 1, description: 'Blue at extremities, body pink (acrocyanosis)' },
      { value: 2, description: 'No cyanosis, body and extremities pink' }
    ]
  },
  {
    name: 'Pulse',
    description: 'Heart rate',
    scores: [
      { value: 0, description: 'Absent' },
      { value: 1, description: 'Below 100 beats per minute' },
      { value: 2, description: 'Above 100 beats per minute' }
    ]
  },
  {
    name: 'Grimace',
    description: 'Reflex irritability',
    scores: [
      { value: 0, description: 'No response to stimulation' },
      { value: 1, description: 'Grimace/feeble cry when stimulated' },
      { value: 2, description: 'Cry or pull away when stimulated' }
    ]
  },
  {
    name: 'Activity',
    description: 'Muscle tone',
    scores: [
      { value: 0, description: 'None, limp' },
      { value: 1, description: 'Some flexion of extremities' },
      { value: 2, description: 'Active motion' }
    ]
  },
  {
    name: 'Respiration',
    description: 'Breathing effort',
    scores: [
      { value: 0, description: 'Absent' },
      { value: 1, description: 'Weak, irregular, gasping' },
      { value: 2, description: 'Strong cry' }
    ]
  }
];

export default function APGARScoringDialog({ isOpen, onClose, onScoreSubmit, timePoint }) {
  const [scores, setScores] = useState({
    Appearance: null,
    Pulse: null,
    Grimace: null,
    Activity: null,
    Respiration: null
  });

  const handleScoreSelect = (criterion, value) => {
    setScores(prev => ({
      ...prev,
      [criterion]: value
    }));
  };

  const calculateTotal = () => {
    return Object.values(scores).reduce((sum, score) => sum + (score || 0), 0);
  };

  const handleSubmit = () => {
    const total = calculateTotal();
    onScoreSubmit(total, timePoint);
    onClose();
  };

  if (!isOpen) return null;

  const total = calculateTotal();
  const allScoresSelected = Object.values(scores).every(score => score !== null);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-lg max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              APGAR Score Calculator - {timePoint} Minute
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <span className="sr-only">Close</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-6">
            {APGARCriteria.map((criterion) => (
              <div key={criterion.name} className="border rounded-lg p-4">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-medium text-gray-900">{criterion.name}</h3>
                  <span className="text-sm text-gray-500">{criterion.description}</span>
                </div>
                <div className="grid grid-cols-1 gap-2">
                  {criterion.scores.map((score) => (
                    <label
                      key={score.value}
                      className={`flex items-center p-3 rounded-lg cursor-pointer transition-colors ${
                        scores[criterion.name] === score.value
                          ? 'bg-emerald-50 border-2 border-emerald-500'
                          : 'border hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name={criterion.name}
                        value={score.value}
                        checked={scores[criterion.name] === score.value}
                        onChange={() => handleScoreSelect(criterion.name, score.value)}
                        className="sr-only"
                      />
                      <div className="flex-1">
                        <div className="flex justify-between">
                          <span className="font-medium">{score.value} points</span>
                          <span className="text-gray-600">{score.description}</span>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 border-t pt-6">
            <div className="flex justify-between items-center mb-4">
              <span className="text-lg font-medium">Total APGAR Score:</span>
              <span className="text-2xl font-bold text-emerald-600">{total}/10</span>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!allScoresSelected}
                className={`px-4 py-2 rounded-lg text-white ${
                  allScoresSelected
                    ? 'bg-emerald-600 hover:bg-emerald-700'
                    : 'bg-gray-300 cursor-not-allowed'
                }`}
              >
                Submit Score
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 